import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { Calendar, Check, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { StickyTimelineScrollbar } from '../../components/StickyTimelineScrollbar'
import { smoothScrollLeftTo } from '../../lib/smooth-scroll'
import { formatDate } from '../../lib/format'

/** Interactive tenant-wide Gantt rendered on the Task Monitor.
 *
 *  Mirrors the SpacePage Gantt's visual language (two-pane layout,
 *  week strip + day header, hatched diagonal weekend shading, today
 *  badge, status-pill group headers, rounded coloured bars) AND the
 *  Manager-Space editing affordances:
 *
 *    - Drag a bar to reschedule (move whole task left/right).
 *    - Drag the left / right edge of a bar to resize start / due.
 *    - Click a bar to open a date popover (Start / Due inputs).
 *    - Click the row's status dot to recolour the bar.
 *
 *  Edits go through the `onUpdateTask(id, patch)` callback the parent
 *  wires to `PATCH /admin/tasks/:id`. Optimistic updates land in the
 *  shared React Query cache so bars track the cursor without waiting
 *  for the round-trip. */

const GROUP_LABEL = {
  department: 'Department',
  assignee: 'Assignee',
  status: 'Status',
  priority: 'Priority',
  none: 'No grouping',
}

const PRIORITY_ORDER = ['urgent', 'high', 'normal', 'low']
const PRIORITY_LABEL = {
  urgent: 'Urgent',
  high: 'High',
  normal: 'Medium',
  low: 'Low',
}

/** Categories that drive the section ordering when grouping by Status.
 *  Mirrors the Task Monitor list view so the two surfaces feel related
 *  when admins flip between them. */
const STATUS_CATEGORY_ORDER = [
  'backlog',
  'not_started',
  'active',
  'review',
  'done',
  'closed',
  'cancelled',
]

const BAR_COLOR_PALETTE = [
  '#94a3b8',
  '#0EA5E9',
  '#3B82F6',
  '#A855F7',
  '#EC4899',
  '#EF4444',
  '#F97316',
  '#F59E0B',
  '#EAB308',
  '#10B981',
  '#14B8A6',
  '#22D3EE',
]

const DAY_MS = 24 * 60 * 60 * 1000

const DAY_W_WEEK_ZOOM = 96
const ROW_H = 44
// Header is three tiers tall: month/year banner, week strip, day
// labels. Each tier is 40px (`HEADER_H / 3`) — enough vertical room for
// the day cell's two lines (weekday + date) to sit clear of the tier
// borders instead of touching them.
const HEADER_H = 120
const NAME_W = 240
const START_DATE_W = 120
const DUE_DATE_W = 120
const ASSIGNEE_W = 140
const LEFT_PANE_W = NAME_W + START_DATE_W + DUE_DATE_W + ASSIGNEE_W

const AVATAR_PALETTE = [
  '#02367B',
  '#34D399',
  '#F87171',
  '#FBBF24',
  '#A78BFA',
  '#F472B6',
  '#0a2349',
  '#FB923C',
]

function avatarColor(name) {
  const safe = typeof name === 'string' && name.length > 0 ? name : '?'
  let hash = 0
  for (let i = 0; i < safe.length; i++) hash = (hash * 31 + safe.charCodeAt(i)) | 0
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length]
}

function initials(name) {
  if (typeof name !== 'string' || name.trim().length === 0) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/** Due-date label for the Gantt left pane.
 *
 *  Canonical app-wide format is DD/MM/YYYY (`lib/format.js`), so we
 *  render the actual date for every row rather than the relative
 *  phrasing the SpacePage list view uses — admins scanning a tenant-
 *  wide schedule need the exact day, not "4 days ago", and the
 *  surrounding colour already signals overdue / today / upcoming.
 *  Returns `{ label, tone }` so the cell can colour itself accordingly. */
function relativeDueLabel(iso) {
  if (!iso) return { label: '—', tone: 'none' }
  const due = parseISO(iso)
  if (!due) return { label: '—', tone: 'none' }
  // Calendar day (the ISO date part) in the person's date format — a
  // "YYYY-MM-DD" string is never zone-shifted by formatDate.
  const label = formatDate(isoDay(due))
  const now = todayUTC()
  const diff = diffDays(now, due)
  let tone = 'upcoming'
  if (diff < 0) tone = 'overdue'
  else if (diff === 0) tone = 'today'
  return { label, tone }
}

const DEFAULT_DURATION_DAYS = 5

function parseISO(iso) {
  if (typeof iso !== 'string') return null
  const head = iso.length > 10 ? iso.slice(0, 10) : iso
  const [y, m, d] = head.split('-').map(Number)
  if (!y || !m || !d) return null
  return new Date(Date.UTC(y, m - 1, d))
}

function isoDay(d) {
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function addDays(d, n) {
  return new Date(d.getTime() + n * DAY_MS)
}

function diffDays(a, b) {
  return Math.round((b.getTime() - a.getTime()) / DAY_MS)
}

function fmtDay(d) {
  return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]
function fmtMonthYearLong(d) {
  return `${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

function weekdayShort(d) {
  return ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][d.getUTCDay()]
}

function todayUTC() {
  const n = new Date()
  return new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate()))
}

function startOfWeek(d) {
  const day = d.getUTCDay()
  const offset = day === 0 ? -6 : 1 - day
  return addDays(d, offset)
}

function isoWeekNumber(d) {
  const t = new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()),
  )
  const dayNum = t.getUTCDay() || 7
  t.setUTCDate(t.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1))
  return Math.ceil(((t.getTime() - yearStart.getTime()) / DAY_MS + 1) / 7)
}

/** Resolves a task's visible bar span. Honours whichever of
 *  `startDate` / `dueDate` exist:
 *
 *    - both set      → start..due (multi-day bar)
 *    - only due      → DEFAULT_DURATION_DAYS-long bar ending on due
 *    - only start    → single-day bar on start (so a start-only task
 *                      isn't invisible just because nobody picked a
 *                      due date yet)
 *    - neither       → returns null; caller filters this out
 *
 *  Mirrors SpacePage's `ganttSpan` behaviour so the two Gantts treat
 *  partial-date tasks the same way. */
function getTaskRange(t) {
  const start = t.startDate ? parseISO(t.startDate) : null
  const end = t.dueDate ? parseISO(t.dueDate) : null
  if (start && end) return { start, end }
  if (end) return { start: addDays(end, -(DEFAULT_DURATION_DAYS - 1)), end }
  if (start) return { start, end: start }
  return null
}

function groupTasks(tasks, groupBy) {
  if (groupBy === 'none') {
    return [
      {
        key: 'all',
        label: 'All tasks',
        color: '#94a3b8',
        tasks,
      },
    ]
  }
  const buckets = new Map()
  for (const t of tasks) {
    const key =
      groupBy === 'department'
        ? t.department || 'No department'
        : groupBy === 'assignee'
          ? t.assigneeName || 'Unassigned'
          : groupBy === 'status'
            ? // Group by status NAME (TO DO / IN PROGRESS / IN REVIEW /
              // COMPLETE), NOT category — so the Gantt sections line up
              // one-for-one with the List view and the Space List page.
              t.statusName || '—'
            : t.priority || 'No priority'
    if (!buckets.has(key)) buckets.set(key, [])
    buckets.get(key).push(t)
  }

  const keys = Array.from(buckets.keys())

  if (groupBy === 'status') {
    // Order sections by the workflow's authored order (TO DO=0, IN
    // PROGRESS=1, …) — the exact sequence the admin arranged in the
    // Workflows editor — so the Gantt matches the List view and the
    // Space List/Board. Category then name are tie-breaks only.
    const metaOf = (key) => {
      const first =
        buckets.get(key).find((t) => t.statusName === key) ??
        buckets.get(key)[0]
      return {
        cat: STATUS_CATEGORY_ORDER.indexOf(first?.statusCategory ?? 'backlog'),
        order: typeof first?.statusOrder === 'number' ? first.statusOrder : 999,
      }
    }
    keys.sort((a, b) => {
      const ma = metaOf(a)
      const mb = metaOf(b)
      if (ma.order !== mb.order) return ma.order - mb.order
      if (ma.cat !== mb.cat) return ma.cat - mb.cat
      return a.localeCompare(b)
    })
  } else if (groupBy === 'priority') {
    keys.sort((a, b) => PRIORITY_ORDER.indexOf(a) - PRIORITY_ORDER.indexOf(b))
  } else {
    keys.sort((a, b) => a.localeCompare(b))
  }

  return keys.map((key) => {
    const items = buckets.get(key)
    let label = key
    let color = '#94a3b8'
    if (groupBy === 'status') {
      const first = items.find((t) => t.statusColor) ?? items[0]
      color = first?.statusColor ?? '#94a3b8'
      // The section label is the status name itself (the map key).
      label = key
    } else if (groupBy === 'priority') {
      label = PRIORITY_LABEL[key] ?? key
    }
    return { key, label, color, tasks: items }
  })
}

function Select({ className, children, ...rest }) {
  return (
    <div className="relative">
      <select
        {...rest}
        className={
          'h-9 appearance-none rounded-md border border-[color:var(--color-rule)] ' +
          'bg-[color:var(--color-card)] text-[color:var(--color-ink)] text-sm ' +
          'pl-3 pr-9 outline-none cursor-pointer ' +
          (className ?? '')
        }
      >
        {children}
      </select>
      <ChevronDown
        size={14}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-muted)] pointer-events-none"
        aria-hidden="true"
      />
    </div>
  )
}

function GroupHeaderPill({ label, color }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider"
      style={{ color }}
    >
      <span
        className="h-2 w-2 rounded-full"
        style={{ backgroundColor: color }}
        aria-hidden="true"
      />
      {label}
    </span>
  )
}

export function TaskMonitorGantt({ tasks, onUpdateTask }) {
  const [groupBy, setGroupBy] = useState('status')
  const [collapsed, setCollapsed] = useState(() => new Set())

  const today = useMemo(() => todayUTC(), [])
  // Week-only zoom — the Day toggle was removed because the admin Task
  // Monitor crosses many workspaces and a day-by-day view becomes
  // overwhelming. Width per day stays the wider 96px so a single week
  // reads at a glance.
  const dayWidth = DAY_W_WEEK_ZOOM

  // A timeline view is only meaningful for tasks that have at LEAST
  // one date. SpacePage's Gantt already renders one-date tasks as a
  // single-day or default-duration bar; Task Monitor mirrors that so a
  // task with `startDate` but no `dueDate` (or vice versa) still
  // appears here instead of vanishing from the admin view.
  const datedTasks = useMemo(
    () =>
      tasks.filter(
        (t) =>
          (typeof t.dueDate === 'string' && t.dueDate) ||
          (typeof t.startDate === 'string' && t.startDate),
      ),
    [tasks],
  )

  // Infinite scroll into past + future. The range starts at ±1 year
  // around today, then extends by another year whenever the user
  // scrolls within `EDGE_DAYS` of either edge. Tasks falling outside
  // the active window expand it too so an off-screen due date still
  // has a bar to land on.
  const EXTEND_BY = 365
  const EDGE_DAYS = 14
  const [pastDays, setPastDays] = useState(365)
  const [futureDays, setFutureDays] = useState(365)

  const { rangeStart, rangeEnd } = useMemo(() => {
    let minStart = addDays(today, -pastDays)
    let maxEnd = addDays(today, futureDays)
    for (const t of datedTasks) {
      const { start, end } = getTaskRange(t)
      if (start < minStart) minStart = addDays(start, -30)
      if (end > maxEnd) maxEnd = addDays(end, 30)
    }
    return { rangeStart: minStart, rangeEnd: maxEnd }
  }, [datedTasks, today, pastDays, futureDays])

  const alignedStart = useMemo(() => startOfWeek(rangeStart), [rangeStart])

  const totalDays = useMemo(
    () => diffDays(alignedStart, rangeEnd) + 1,
    [alignedStart, rangeEnd],
  )

  const days = useMemo(() => {
    const out = []
    for (let i = 0; i < totalDays; i++) {
      out.push(addDays(alignedStart, i))
    }
    return out
  }, [alignedStart, totalDays])

  const weeks = useMemo(() => {
    const out = []
    for (let i = 0; i < days.length; i += 7) {
      const start = days[i]
      const span = Math.min(7, days.length - i)
      const end = addDays(start, span - 1)
      out.push({
        key: `w-${i}`,
        span,
        label: `W${isoWeekNumber(start)} · ${fmtDay(start)}–${fmtDay(end)}`,
      })
    }
    return out
  }, [days])

  /** Month/year banner — stitches consecutive day columns into
   *  whatever month they share. Each entry says "May 2026" and spans
   *  that month's day count, so as the user scrolls the year is
   *  always visible at the top of the scroll surface. */
  const months = useMemo(() => {
    if (days.length === 0) return []
    const out = []
    let runStart = 0
    let runMonth = days[0].getUTCMonth()
    let runYear = days[0].getUTCFullYear()
    for (let i = 1; i < days.length; i++) {
      const m = days[i].getUTCMonth()
      const y = days[i].getUTCFullYear()
      if (m !== runMonth || y !== runYear) {
        out.push({
          key: `m-${runStart}`,
          span: i - runStart,
          label: fmtMonthYearLong(days[runStart]),
        })
        runStart = i
        runMonth = m
        runYear = y
      }
    }
    out.push({
      key: `m-${runStart}`,
      span: days.length - runStart,
      label: fmtMonthYearLong(days[runStart]),
    })
    return out
  }, [days])

  const TIMELINE_W = days.length * dayWidth

  const groups = useMemo(
    // Group over ALL tasks (not only dated ones) so undated tasks still
    // appear and are counted, matching the List/Board views. They render as
    // a bar-less row (guarded in the timeline below); the scroll range still
    // derives from `datedTasks` only.
    () => groupTasks(tasks, groupBy),
    [tasks, groupBy],
  )

  function toggleGroup(key) {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function xForDate(date) {
    return diffDays(alignedStart, date) * dayWidth
  }

  // ── Drag-to-reschedule ─────────────────────────────────────────────
  // `dragPreview` tracks which task is being dragged, the mode
  // (move/resize-start/resize-end), and the current cursor delta in
  // days. Bars subtract this preview when rendering so the bar
  // visually follows the cursor while the underlying data is
  // unchanged — the mutation only fires once on pointerup.
  const [dragPreview, setDragPreview] = useState(null)

  function startBarDrag(e, task, mode) {
    if (!onUpdateTask) return
    e.preventDefault()
    e.stopPropagation()
    const range = getTaskRange(task)
    const startX = e.clientX
    const startDate = range.start
    const dueDate = range.end
    let lastDeltaDays = 0
    let moved = false

    function onMove(ev) {
      const dx = ev.clientX - startX
      const deltaDays = Math.round(dx / dayWidth)
      if (Math.abs(dx) > 4) moved = true
      if (deltaDays !== lastDeltaDays) {
        lastDeltaDays = deltaDays
        setDragPreview({ taskId: task.id, mode, deltaDays })
      }
    }

    function onUp() {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      setDragPreview(null)
      if (!moved || lastDeltaDays === 0) return

      const patch = {}
      if (mode === 'move') {
        patch.startDate = isoDay(addDays(startDate, lastDeltaDays))
        patch.dueDate = isoDay(addDays(dueDate, lastDeltaDays))
      } else if (mode === 'resize-start') {
        const nextStart = addDays(startDate, lastDeltaDays)
        if (nextStart.getTime() > dueDate.getTime()) return
        patch.startDate = isoDay(nextStart)
      } else if (mode === 'resize-end') {
        const nextDue = addDays(dueDate, lastDeltaDays)
        if (nextDue.getTime() < startDate.getTime()) return
        patch.dueDate = isoDay(nextDue)
      }
      onUpdateTask(task.id, patch)
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  function barLeftWidth(t) {
    const { start, end } = getTaskRange(t)
    let s = start
    let e = end
    if (dragPreview && dragPreview.taskId === t.id) {
      const d = dragPreview.deltaDays
      if (dragPreview.mode === 'move') {
        s = addDays(start, d)
        e = addDays(end, d)
      } else if (dragPreview.mode === 'resize-start') {
        s = addDays(start, d)
        if (s.getTime() > e.getTime()) s = e
      } else if (dragPreview.mode === 'resize-end') {
        e = addDays(end, d)
        if (e.getTime() < s.getTime()) e = s
      }
    }
    const left = xForDate(s)
    const lengthDays = diffDays(s, e) + 1
    const width = Math.max(lengthDays * dayWidth - 4, 8)
    return { left, width, previewStart: s, previewEnd: e }
  }

  const todayOffset = useMemo(() => xForDate(today), [today, alignedStart, dayWidth]) // eslint-disable-line react-hooks/exhaustive-deps
  const todayInRange = todayOffset >= 0 && todayOffset <= TIMELINE_W

  // Centre today on first mount. We gate this with a ref so subsequent
  // re-renders (filter changes, task patches, range extensions while
  // the user is scrolling) don't yank the viewport back to today.
  const rightPaneRef = useRef(null)
  // Inner track of the date header. It lives OUTSIDE the horizontal
  // scroller (an ancestor with `overflow-x` is a scroll container that
  // traps `position: sticky`, so the header would scroll out of view
  // vertically). Kept in horizontal lock-step with the bars by mirroring
  // the bars' scrollLeft onto this element as a transform — see `syncHeader`.
  const headerInnerRef = useRef(null)
  const syncHeader = useCallback((scrollLeft) => {
    const h = headerInnerRef.current
    if (h) h.style.transform = `translateX(${-scrollLeft}px)`
  }, [])

  const hasCentredRef = useRef(false)
  useLayoutEffect(() => {
    if (hasCentredRef.current) return
    const el = rightPaneRef.current
    if (!el) return
    const visibleWidth = el.clientWidth
    el.scrollLeft = Math.max(0, todayOffset - visibleWidth / 2)
    syncHeader(el.scrollLeft)
    hasCentredRef.current = true
  })

  // Smooth-scroll today's column back to centre — backs the toolbar
  // "Today" button so the user can return to the current date after
  // panning across the canvas.
  function scrollToToday() {
    const el = rightPaneRef.current
    if (!el) return
    // Fixed-duration rAF tween — smooth at any distance (native smooth
    // scroll stutters across this wide canvas). Today is always far from
    // the edges, so this never trips the infinite-scroll extend.
    smoothScrollLeftTo(el, todayOffset - el.clientWidth / 2)
  }

  // When the past edge extends, alignedStart shifts to an earlier
  // date and every existing bar moves to the right inside the new
  // wider canvas. To make the user feel they stayed put, we add
  // back the pixel delta to scrollLeft right before paint.
  const prevAlignedStartRef = useRef(alignedStart)
  useLayoutEffect(() => {
    const prev = prevAlignedStartRef.current
    if (prev.getTime() === alignedStart.getTime()) return
    const el = rightPaneRef.current
    if (el) {
      const shiftDays = diffDays(alignedStart, prev)
      if (shiftDays > 0) {
        el.scrollLeft += shiftDays * dayWidth
        syncHeader(el.scrollLeft)
      }
    }
    prevAlignedStartRef.current = alignedStart
  }, [alignedStart, dayWidth, syncHeader])

  // Viewport state — drives the off-screen indicator chevrons that
  // appear at the visible left/right edge of the timeline when a
  // task's bar sits entirely outside the current scroll window.
  // Clicking either arrow jumps the timeline to the bar's start so
  // the user can return to a task's actual position after scrolling
  // far into the past or future.
  const [viewport, setViewport] = useState({ scrollLeft: 0, clientWidth: 0 })
  const viewportRafRef = useRef(null)

  useEffect(() => {
    const el = rightPaneRef.current
    if (!el) return
    const measure = () => {
      setViewport({ scrollLeft: el.scrollLeft, clientWidth: el.clientWidth })
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Scroll handler — extend the active range when the user gets near
  // either edge. Uses requestAnimationFrame-style guard to avoid
  // triggering the extension multiple times for the same scroll
  // event (React batches state updates anyway, but the guard
  // protects against jank in slow renders). Also drives an rAF-
  // throttled viewport state update for the off-screen chevrons.
  const extendingRef = useRef(false)
  const onScroll = useCallback(
    (e) => {
      const el = e.currentTarget
      // Keep the lifted-out date header aligned with the bars. Done
      // synchronously (not via React state) so it never lags the scroll.
      syncHeader(el.scrollLeft)
      if (viewportRafRef.current == null) {
        viewportRafRef.current = requestAnimationFrame(() => {
          viewportRafRef.current = null
          setViewport({
            scrollLeft: el.scrollLeft,
            clientWidth: el.clientWidth,
          })
        })
      }
      if (extendingRef.current) return
      const threshold = dayWidth * EDGE_DAYS
      const remainingRight = el.scrollWidth - el.scrollLeft - el.clientWidth
      if (el.scrollLeft < threshold) {
        extendingRef.current = true
        setPastDays((p) => p + EXTEND_BY)
        requestAnimationFrame(() => {
          extendingRef.current = false
        })
        return
      }
      if (remainingRight < threshold) {
        extendingRef.current = true
        setFutureDays((f) => f + EXTEND_BY)
        requestAnimationFrame(() => {
          extendingRef.current = false
        })
      }
    },
    [dayWidth, syncHeader],
  )

  // Per-parent expand/collapse for Gantt subtasks. Default-collapsed
  // so the timeline doesn't drown in subtasks on first render.
  const [expandedParents, setExpandedParents] = useState(() => new Set())
  function toggleParent(taskId) {
    setExpandedParents((prev) => {
      const next = new Set(prev)
      if (next.has(taskId)) next.delete(taskId)
      else next.add(taskId)
      return next
    })
  }

  const visibleRows = useMemo(() => {
    const rows = []
    for (const g of groups) {
      rows.push({ kind: 'group', group: g })
      if (collapsed.has(g.key)) continue
      // Split tasks in this group into TOP-LEVEL parents and a
      // subtask-by-parent lookup. Parents render first; subtasks
      // render nested under their parent when that parent is
      // expanded. Subtasks whose parent isn't in THIS group (e.g.
      // parent fell into a different status / department bucket)
      // are surfaced as orphan top-level rows so they never vanish.
      const parents = []
      const subsByParent = new Map()
      for (const t of g.tasks) {
        if (t.parentTaskId) {
          const k = String(t.parentTaskId)
          const arr = subsByParent.get(k) ?? []
          arr.push(t)
          subsByParent.set(k, arr)
        } else {
          parents.push(t)
        }
      }
      const consumedSubIds = new Set()
      for (const t of parents) {
        const subs = subsByParent.get(String(t.id)) ?? []
        const isExpanded = expandedParents.has(t.id)
        rows.push({
          kind: 'task',
          task: t,
          group: g,
          depth: 0,
          subCount: subs.length,
          expanded: isExpanded,
        })
        for (const sub of subs) consumedSubIds.add(String(sub.id))
        if (subs.length > 0 && isExpanded) {
          for (const sub of subs) {
            rows.push({
              kind: 'task',
              task: sub,
              group: g,
              depth: 1,
              subCount: 0,
              expanded: false,
            })
          }
        }
      }
      // Orphan subtasks — their parent isn't in this group.
      for (const subs of subsByParent.values()) {
        for (const sub of subs) {
          if (consumedSubIds.has(String(sub.id))) continue
          rows.push({
            kind: 'task',
            task: sub,
            group: g,
            depth: 0,
            subCount: 0,
            expanded: false,
          })
        }
      }
    }
    return rows
  }, [groups, collapsed, expandedParents])

  // ── Date popover (Start / Due) and Colour picker ──────────────────
  // One instance of each at the GanttView level, anchored to whichever
  // trigger most recently opened it. Position is tracked via state so
  // the popover survives bar re-renders when the cursor moves.
  const [editingTaskId, setEditingTaskId] = useState(null)
  const [datePopoverAnchor, setDatePopoverAnchor] = useState(null)
  const [colorPickerTaskId, setColorPickerTaskId] = useState(null)
  const [colorPickerAnchor, setColorPickerAnchor] = useState(null)

  const editingTask = useMemo(
    () => tasks.find((t) => t.id === editingTaskId) ?? null,
    [tasks, editingTaskId],
  )
  const colorPickerTask = useMemo(
    () => tasks.find((t) => t.id === colorPickerTaskId) ?? null,
    [tasks, colorPickerTaskId],
  )

  function openDateEditor(triggerEl, task) {
    if (!onUpdateTask) return
    setDatePopoverAnchor(triggerEl)
    setEditingTaskId(task.id)
  }

  function openColorPicker(triggerEl, task) {
    if (!onUpdateTask) return
    setColorPickerAnchor(triggerEl)
    setColorPickerTaskId(task.id)
  }

  return (
    <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
      <div className="px-4 py-3 border-b border-[color:var(--color-rule)] flex flex-wrap items-center gap-3 bg-[color:var(--color-canvas)]">
        <label className="text-sm text-[color:var(--color-ink-muted)] flex items-center gap-2">
          Group by
          <Select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
            {Object.keys(GROUP_LABEL).map((g) => (
              <option key={g} value={g}>
                {GROUP_LABEL[g]}
              </option>
            ))}
          </Select>
        </label>
        {/* Jump back to today's column after panning across the canvas. */}
        <button
          type="button"
          onClick={scrollToToday}
          title="Jump to today"
          className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-2.5 py-1 text-xs font-medium text-[color:var(--color-ink)] shadow-sm hover:border-[color:var(--color-accent)] hover:text-[color:var(--color-accent)] cursor-pointer"
        >
          <Calendar size={13} />
          Today
        </button>
      </div>

      {/* Vertical scroll lives on the FLEX parent so both panes scroll
       *  together in lock-step (rows stay aligned). The right pane only
       *  handles HORIZONTAL scroll, and its native horizontal scrollbar
       *  is hidden — the `StickyTimelineScrollbar` mounted below pins a
       *  visible scrollbar to the viewport bottom, matching the Spaces /
       *  Team Tasks / My Tasks Gantts. */}
      <div className="flex max-h-[70vh] overflow-y-auto">
        <div
          className="shrink-0 border-r border-[color:var(--color-rule)] bg-[color:var(--color-card)]"
          style={{ width: LEFT_PANE_W }}
        >
          {/* Four-column header: Name + Start Date + Due Date + Assignee.
           *  Mirrors the Manager Space Gantt's left pane and matches
           *  the Task Monitor List view's column order. */}
          <div
            className="flex items-end bg-[color:var(--color-canvas)] border-b border-[color:var(--color-rule)] text-sm font-medium text-[color:var(--color-ink-muted)] sticky top-0 z-10"
            style={{ height: HEADER_H }}
          >
            <div className="px-3 py-2 truncate" style={{ width: NAME_W }}>
              {GROUP_LABEL[groupBy]} / Task
            </div>
            <div className="px-3 py-2" style={{ width: START_DATE_W }}>
              Start Date
            </div>
            <div className="px-3 py-2" style={{ width: DUE_DATE_W }}>
              Due Date
            </div>
            <div className="px-3 py-2" style={{ width: ASSIGNEE_W }}>
              Assignee
            </div>
          </div>
          {visibleRows.length === 0 ? (
            <div className="px-4 py-6 text-sm text-[color:var(--color-ink-muted)]">
              No tasks to chart.
            </div>
          ) : (
            visibleRows.map((row, idx) => {
              if (row.kind === 'group') {
                const isCollapsed = collapsed.has(row.group.key)
                return (
                  <div
                    key={`name-group-${row.group.key}-${idx}`}
                    className="flex bg-[color:var(--color-canvas)] border-b border-[color:var(--color-rule)]"
                    style={{ height: ROW_H }}
                  >
                    <button
                      type="button"
                      onClick={() => toggleGroup(row.group.key)}
                      className="flex items-center gap-2 px-3 cursor-pointer hover:bg-[color:var(--color-card-hover)] text-left"
                      style={{ width: NAME_W, height: ROW_H }}
                      aria-expanded={!isCollapsed}
                    >
                      <ChevronDown
                        size={14}
                        className={
                          'shrink-0 transition-transform text-[color:var(--color-ink-muted)] ' +
                          (isCollapsed ? '-rotate-90' : '')
                        }
                      />
                      <GroupHeaderPill
                        label={row.group.label}
                        color={row.group.color}
                      />
                      {/* Count sits right after the group pill. Was
                       *  `ml-auto`, which shoved it to the far-right edge
                       *  of the fixed-width Name column — a big gap for
                       *  short status/priority pills. */}
                      <span className="text-sm text-[color:var(--color-ink-muted)] tabular-nums">
                        {row.group.tasks.length}
                      </span>
                    </button>
                    {/* Filler cells keep group separator full-width so
                     *  it lines up across all four columns. */}
                    <div style={{ width: START_DATE_W }} aria-hidden="true" />
                    <div style={{ width: DUE_DATE_W }} aria-hidden="true" />
                    <div style={{ width: ASSIGNEE_W }} aria-hidden="true" />
                  </div>
                )
              }
              const t = row.task
              const due = relativeDueLabel(t.dueDate)
              const dueTone =
                due.tone === 'overdue'
                  ? 'text-[color:var(--color-priority-urgent)]'
                  : due.tone === 'today'
                    ? 'text-[color:var(--color-priority-high)]'
                    : 'text-[color:var(--color-ink-muted)]'
              const startLabel = t.startDate
                ? (() => {
                    const d = parseISO(t.startDate)
                    if (!d) return '—'
                    return formatDate(isoDay(d))
                  })()
                : '—'
              const depth = row.depth ?? 0
              const subCount = row.subCount ?? 0
              const taskExpanded = row.expanded ?? false
              const hasSubs = subCount > 0
              return (
                <div
                  key={`name-task-${t.id}`}
                  className="flex border-b border-[color:var(--color-rule)] text-base text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]"
                  style={{ height: ROW_H }}
                >
                  <div
                    className="flex items-center gap-2 truncate"
                    style={{ width: NAME_W, paddingLeft: 12 + depth * 24, paddingRight: 12 }}
                    title={t.name}
                  >
                    {hasSubs ? (
                      <button
                        type="button"
                        onClick={() => toggleParent(t.id)}
                        aria-expanded={taskExpanded}
                        title={
                          taskExpanded ? 'Collapse subtasks' : 'Expand subtasks'
                        }
                        className="p-0.5 rounded hover:bg-[color:var(--color-card-hover)] cursor-pointer text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)]"
                      >
                        <ChevronRight
                          size={12}
                          className={
                            'shrink-0 transition-transform ' +
                            (taskExpanded ? 'rotate-90' : '')
                          }
                        />
                      </button>
                    ) : (
                      <span className="w-3 shrink-0" aria-hidden="true" />
                    )}
                    <button
                      type="button"
                      onClick={(e) => openColorPicker(e.currentTarget, t)}
                      aria-label="Change bar colour"
                      title="Change bar colour"
                      className="h-2.5 w-2.5 rounded-full shrink-0 cursor-pointer motion-safe:hover:scale-110 transition-transform"
                      style={{
                        backgroundColor:
                          t.color || t.statusColor || '#94a3b8',
                      }}
                    />
                    <span className="truncate">{t.name}</span>
                    {hasSubs ? (
                      <span className="text-[10px] tabular-nums text-[color:var(--color-ink-muted)] bg-[color:var(--color-canvas)] border border-[color:var(--color-rule)] rounded-full px-1.5 py-0.5 shrink-0">
                        {subCount}
                      </span>
                    ) : null}
                  </div>
                  <div
                    className="flex items-center px-3 text-sm text-[color:var(--color-ink-muted)]"
                    style={{ width: START_DATE_W }}
                  >
                    {startLabel}
                  </div>
                  <div
                    className={'flex items-center px-3 text-sm ' + dueTone}
                    style={{ width: DUE_DATE_W }}
                  >
                    {due.label}
                  </div>
                  <div
                    className="flex items-center gap-2 px-3 truncate"
                    style={{ width: ASSIGNEE_W }}
                  >
                    {t.assigneeName ? (
                      <>
                        <span
                          className="inline-flex items-center justify-center h-7 w-7 rounded-full text-xs font-medium text-white shrink-0"
                          style={{ backgroundColor: avatarColor(t.assigneeName) }}
                          aria-hidden="true"
                        >
                          {initials(t.assigneeName)}
                        </span>
                        <span
                          className="truncate text-sm text-[color:var(--color-ink)]"
                          title={t.assigneeName}
                        >
                          {t.assigneeName}
                        </span>
                      </>
                    ) : (
                      <span className="text-sm text-[color:var(--color-ink-muted)]">
                        Unassigned
                      </span>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>

        <div className="flex-1 min-w-0 relative">
          {/* Date header lifted OUT of the horizontal scroller below.
           *  An ancestor with `overflow-x` is a scroll container that
           *  traps `position: sticky`, so the header used to scroll out
           *  of view vertically and leave the bars with no dates above
           *  them. In this `relative` (non-scrolling) wrapper it pins to
           *  the OUTER vertical scroller just like the left pane header;
           *  `onScroll` mirrors the bars' scrollLeft onto `headerInnerRef`
           *  so it still pans horizontally in lock-step with the bars. */}
          <div
            className="sticky top-0 z-20 overflow-hidden bg-[color:var(--color-canvas)] border-b border-[color:var(--color-rule)]"
            style={{ height: HEADER_H }}
          >
            <div ref={headerInnerRef} style={{ width: TIMELINE_W }}>
              {/* Month/year banner — always visible so the user can
               *  tell which year they're looking at while scrolling
               *  through the infinite past/future canvas. */}
              <div
                className="flex border-b border-[color:var(--color-rule)]"
                style={{ height: HEADER_H / 3 }}
              >
                {months.map((m) => (
                  <div
                    key={m.key}
                    className="border-r border-[color:var(--color-rule)] flex items-center px-3 text-xs font-medium text-[color:var(--color-ink-strong)] truncate bg-[color:var(--color-canvas)]"
                    style={{ width: m.span * dayWidth, minWidth: m.span * dayWidth }}
                    title={m.label}
                  >
                    {m.label}
                  </div>
                ))}
              </div>
              <div
                className="flex border-b border-[color:var(--color-rule)]"
                style={{ height: HEADER_H / 3 }}
              >
                {weeks.map((w) => (
                  <div
                    key={w.key}
                    className="border-r border-[color:var(--color-rule)] flex items-center px-2 text-xs font-medium text-[color:var(--color-ink-muted)] truncate"
                    style={{ width: w.span * dayWidth, minWidth: w.span * dayWidth }}
                  >
                    {w.label}
                  </div>
                ))}
              </div>
              <div className="flex" style={{ height: HEADER_H / 3 }}>
                {days.map((d, i) => {
                  const isToday = d.getTime() === today.getTime()
                  return (
                    <div
                      key={i}
                      className="shrink-0 border-r border-[color:var(--color-rule)] flex flex-col items-center justify-center gap-0.5 leading-none text-[11px] text-[color:var(--color-ink-muted)]"
                      style={{ width: dayWidth }}
                    >
                      <span className="uppercase tracking-wider">
                        {weekdayShort(d)}
                      </span>
                      {isToday ? (
                        <span
                          className="mt-0.5 inline-flex items-center justify-center rounded-full text-white text-[11px] font-medium px-1.5 py-0.5"
                          style={{ backgroundColor: 'var(--color-accent)' }}
                        >
                          {fmtDay(d)}
                        </span>
                      ) : (
                        <span>{fmtDay(d)}</span>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          <div
            ref={rightPaneRef}
            onScroll={onScroll}
            className="overflow-x-auto relative view-hscroll"
          >
            <div style={{ width: TIMELINE_W, position: 'relative' }}>
            {todayInRange ? (
              <div
                aria-hidden="true"
                className="absolute pointer-events-none"
                style={{
                  top: 0,
                  bottom: 0,
                  left: todayOffset + dayWidth / 2,
                  width: 2,
                  backgroundColor: 'var(--color-accent)',
                  opacity: 0.45,
                }}
              />
            ) : null}

            {visibleRows.length === 0 ? (
              <div className="px-4 py-10 text-sm text-[color:var(--color-ink-muted)]">
                No data — bars will appear here once tasks are loaded.
              </div>
            ) : (
              visibleRows.map((row, idx) => {
                if (row.kind === 'group') {
                  return (
                    <div
                      key={`row-group-${row.group.key}-${idx}`}
                      className="relative border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]"
                      style={{ height: ROW_H }}
                    >
                      {/* Weekend hatching removed. */}
                    </div>
                  )
                }
                const t = row.task
                // Undated task — no date to anchor a bar. Render an empty
                // timeline row so it stays aligned with its left-panel row
                // (and counted) rather than vanishing from the Gantt.
                if (!getTaskRange(t)) {
                  return (
                    <div
                      key={`row-task-${t.id}`}
                      className="relative flex items-center border-b border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)]"
                      style={{ height: ROW_H }}
                    />
                  )
                }
                const { left, width, previewStart, previewEnd } = barLeftWidth(t)
                const color = t.color || t.statusColor || '#94a3b8'
                const tooltip = `${t.name} — ${t.statusName ?? ''} · ${fmtDay(previewStart)} → ${fmtDay(previewEnd)}`
                const interactive = Boolean(onUpdateTask)
                const showLeftChevron =
                  viewport.clientWidth > 0 &&
                  left + width <= viewport.scrollLeft
                const showRightChevron =
                  viewport.clientWidth > 0 &&
                  left >= viewport.scrollLeft + viewport.clientWidth
                return (
                  <div
                    key={`row-task-${t.id}`}
                    className="relative flex items-center border-b border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)]"
                    style={{ height: ROW_H }}
                  >
                    {/* Weekend hatching removed. */}
                    {/* Sticky off-screen indicator chevrons — flex
                     *  children pinned to the viewport edges by CSS
                     *  sticky. The browser handles the pinning
                     *  natively so the chevron position never lags
                     *  the scroll. */}
                    {showLeftChevron ? (
                      <button
                        type="button"
                        onClick={() => {
                          rightPaneRef.current?.scrollTo({
                            left: Math.max(0, left - 40),
                            behavior: 'smooth',
                          })
                        }}
                        title={`Jump to ${t.name}`}
                        aria-label={`Jump to ${t.name}`}
                        className="sticky shrink-0 z-10 flex items-center justify-center h-6 w-6 ml-1.5 rounded bg-[color:var(--color-card)] border border-[color:var(--color-rule)] text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-ink-strong)] cursor-pointer shadow-sm"
                        style={{ left: 6 }}
                      >
                        <ChevronLeft size={14} />
                      </button>
                    ) : null}
                    <div className="flex-1" />
                    {showRightChevron ? (
                      <button
                        type="button"
                        onClick={() => {
                          rightPaneRef.current?.scrollTo({
                            left: Math.max(0, left - 40),
                            behavior: 'smooth',
                          })
                        }}
                        title={`Jump to ${t.name}`}
                        aria-label={`Jump to ${t.name}`}
                        className="sticky shrink-0 z-10 flex items-center justify-center h-6 w-6 mr-1.5 rounded bg-[color:var(--color-card)] border border-[color:var(--color-rule)] text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-ink-strong)] cursor-pointer shadow-sm"
                        style={{ right: 6 }}
                      >
                        <ChevronRight size={14} />
                      </button>
                    ) : null}
                    <div
                      title={tooltip}
                      onPointerDown={
                        interactive
                          ? (ev) => startBarDrag(ev, t, 'move')
                          : undefined
                      }
                      onClick={
                        interactive
                          ? (ev) => {
                              // Only fire the popover if the user
                              // didn't drag (drag clears pointer state
                              // before click); a short pointerup with
                              // no movement still bubbles up here.
                              if (dragPreview) return
                              openDateEditor(ev.currentTarget, t)
                            }
                          : undefined
                      }
                      className={
                        'absolute rounded-md text-sm text-white font-normal px-2 truncate flex items-center shadow-sm select-none ' +
                        (interactive ? 'cursor-grab active:cursor-grabbing' : 'cursor-default')
                      }
                      style={{
                        left,
                        width,
                        top: 6,
                        height: ROW_H - 12,
                        backgroundColor: color,
                        boxShadow: `inset 3px 0 0 0 rgba(0,0,0,0.18)`,
                        // `pan-x pan-y` so a touch swipe scrolls the timeline
                        // rather than being captured by the bar's pointer-based
                        // drag-to-reschedule; mouse drag is unaffected (touch-
                        // action is touch-only). Mirrors the Spaces Gantt bars.
                        touchAction: 'pan-x pan-y',
                      }}
                    >
                      {/* Left resize handle — 6px hit area on the bar's
                       *  left edge. Cursor changes to col-resize so
                       *  users discover the affordance. */}
                      {interactive ? (
                        <span
                          onPointerDown={(ev) =>
                            startBarDrag(ev, t, 'resize-start')
                          }
                          className="absolute inset-y-0 left-0 w-2 cursor-col-resize"
                          aria-hidden="true"
                        />
                      ) : null}
                      {/* Task-name label inside the bar removed —
                       *  the row's Status / Task column already
                       *  shows it, so the bar reads as a pure date
                       *  span (consistent with the Spaces / Team
                       *  Tasks / My Tasks Gantts). */}
                      {interactive ? (
                        <span
                          onPointerDown={(ev) =>
                            startBarDrag(ev, t, 'resize-end')
                          }
                          className="absolute inset-y-0 right-0 w-2 cursor-col-resize"
                          aria-hidden="true"
                        />
                      ) : null}
                    </div>
                  </div>
                )
              })
            )}
            </div>
          </div>
        </div>
      </div>

      {/* Date editor popover — anchored to whichever bar was clicked. */}
      {editingTask && datePopoverAnchor ? (
        <DateEditorPopover
          anchor={datePopoverAnchor}
          task={editingTask}
          onCommit={(patch) => {
            onUpdateTask(editingTask.id, patch)
            setEditingTaskId(null)
            setDatePopoverAnchor(null)
          }}
          onClose={() => {
            setEditingTaskId(null)
            setDatePopoverAnchor(null)
          }}
        />
      ) : null}

      {/* Colour picker — palette of swatches anchored to the row dot. */}
      {colorPickerTask && colorPickerAnchor ? (
        <ColorPickerPopover
          anchor={colorPickerAnchor}
          currentColor={colorPickerTask.color || colorPickerTask.statusColor || '#94a3b8'}
          onPick={(c) => {
            onUpdateTask(colorPickerTask.id, { color: c })
            setColorPickerTaskId(null)
            setColorPickerAnchor(null)
          }}
          onClose={() => {
            setColorPickerTaskId(null)
            setColorPickerAnchor(null)
          }}
        />
      ) : null}

      {/* Sticky horizontal scrollbar pinned to the viewport bottom —
       *  mirrors the Spaces / Team Tasks / My Tasks Gantts. Hides the
       *  native horizontal scrollbar on the right pane and renders a
       *  viewport-anchored portal scrollbar instead so the bar stays
       *  reachable without scrolling the page first. */}
      <StickyTimelineScrollbar
        timelineRef={rightPaneRef}
        timelineWidth={TIMELINE_W}
      />
    </div>
  )
}

/** Anchored popover that lets the admin pick Start and Due dates for a
 *  task with two native date inputs. Portalled to body so it floats
 *  over the Gantt's scroll panes. */
function DateEditorPopover({ anchor, task, onCommit, onClose }) {
  const [coords, setCoords] = useState(null)
  const menuRef = useRef(null)
  const [startDate, setStartDate] = useState(task.startDate ?? '')
  const [dueDate, setDueDate] = useState(task.dueDate ?? '')
  const uid = useId()
  const startDateId = `${uid}-start-date`
  const dueDateId = `${uid}-due-date`

  useLayoutEffect(() => {
    if (!anchor) return
    function update() {
      const rect = anchor.getBoundingClientRect()
      setCoords({ top: rect.bottom + 6, left: rect.left })
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [anchor])

  useEffect(() => {
    function onClick(e) {
      if (menuRef.current?.contains(e.target)) return
      if (anchor && anchor.contains && anchor.contains(e.target)) return
      onClose()
    }
    function onKey(e) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [anchor, onClose])

  if (!coords) return null
  return createPortal(
    <div
      ref={menuRef}
      role="dialog"
      aria-label="Edit task dates"
      style={{
        position: 'fixed',
        top: coords.top,
        left: coords.left,
        zIndex: 100,
      }}
      className="w-[16rem] p-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl"
    >
      <p className="text-xs uppercase tracking-wider font-medium text-[color:var(--color-ink-muted)] mb-2">
        Dates
      </p>
      {/* Labels unwrapped from their inputs: a wrapping <label> makes its
       *  whole box a click target, so clicking above the box — the empty
       *  space beside the caption, or the gap under it — focused the date
       *  input and started editing (QA). An explicit htmlFor label sized
       *  `w-fit` keeps the association while limiting the hit area to the
       *  visible caption text. */}
      <div className="mb-2">
        <label
          htmlFor={startDateId}
          className="block w-fit text-[12px] text-[color:var(--color-ink-muted)]"
        >
          Start Date
        </label>
        <input
          id={startDateId}
          type="date"
          value={startDate || ''}
          max={dueDate || undefined}
          onChange={(e) => setStartDate(e.target.value)}
          className="mt-1 w-full h-9 px-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)]"
        />
      </div>
      <div className="mb-3">
        <label
          htmlFor={dueDateId}
          className="block w-fit text-[12px] text-[color:var(--color-ink-muted)]"
        >
          Due Date
        </label>
        <input
          id={dueDateId}
          type="date"
          value={dueDate || ''}
          min={startDate || undefined}
          onChange={(e) => setDueDate(e.target.value)}
          className="mt-1 w-full h-9 px-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)]"
        />
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="h-8 px-3 rounded-md text-sm border border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => {
            const patch = {}
            if (startDate !== (task.startDate ?? '')) {
              patch.startDate = startDate || null
            }
            if (dueDate !== (task.dueDate ?? '')) {
              patch.dueDate = dueDate || null
            }
            if (Object.keys(patch).length === 0) {
              onClose()
              return
            }
            onCommit(patch)
          }}
          className="h-8 px-3 rounded-md text-sm bg-[color:var(--color-accent)] text-white hover:opacity-90 cursor-pointer"
        >
          Save
        </button>
      </div>
    </div>,
    document.body,
  )
}

/** Swatch popover anchored to the row dot — picks a bar `color` that
 *  overrides the status colour for that one task. `null` would clear
 *  the override but we don't expose that here; admins can just pick
 *  the status colour from the palette if they want it back. */
function ColorPickerPopover({ anchor, currentColor, onPick, onClose }) {
  const [coords, setCoords] = useState(null)
  const menuRef = useRef(null)

  useLayoutEffect(() => {
    if (!anchor) return
    function update() {
      const rect = anchor.getBoundingClientRect()
      setCoords({ top: rect.bottom + 6, left: rect.left })
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [anchor])

  useEffect(() => {
    function onClick(e) {
      if (menuRef.current?.contains(e.target)) return
      if (anchor && anchor.contains && anchor.contains(e.target)) return
      onClose()
    }
    function onKey(e) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [anchor, onClose])

  if (!coords) return null
  return createPortal(
    <div
      ref={menuRef}
      role="dialog"
      aria-label="Bar colour"
      style={{
        position: 'fixed',
        top: coords.top,
        left: coords.left,
        zIndex: 100,
      }}
      className="w-[12.5rem] p-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl"
    >
      <p className="text-xs uppercase tracking-wider font-medium text-[color:var(--color-ink-muted)] mb-2">
        Bar colour
      </p>
      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: 'repeat(6, minmax(0, 1fr))' }}
      >
        {BAR_COLOR_PALETTE.map((c) => {
          const active = c.toLowerCase() === (currentColor || '').toLowerCase()
          return (
            <button
              key={c}
              type="button"
              onClick={() => onPick(c)}
              aria-label={`Pick ${c}`}
              title={c}
              className={
                'relative h-5 w-5 rounded-full transition-transform cursor-pointer motion-safe:hover:scale-110 ' +
                (active
                  ? 'ring-2 ring-offset-1 ring-[color:var(--color-ink-strong)]'
                  : '')
              }
              style={{ backgroundColor: c }}
            >
              {active ? (
                <Check
                  size={10}
                  className="absolute inset-0 m-auto text-white drop-shadow"
                  aria-hidden="true"
                />
              ) : null}
            </button>
          )
        })}
      </div>
    </div>,
    document.body,
  )
}
